import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { BETAALDE_PLANNEN, MODULES, MODULE_LABEL, MODULE_UITLEG, PLAN_LABEL, PLAN_LIMIET, fmtDag, type BetaaldPlan, type Licentie, type Module } from '@ep/core';
import { api } from '../lib/api';
import { useActie } from '../lib/data';
import { toast } from '../ui/toast';
import { Badge, Laden, Veld } from '../ui/ui';

interface AbonnementInfo {
  abonnement: 'TRIAL' | 'STRIPE' | 'HANDMATIG';
  plan: string;
  gekozenModules: string[];
  status: string | null;
  betaalwijze: 'AUTOMATISCH' | 'FACTUUR' | null;
  trialTot: string | null;
  betaaldTot: string | null;
  opzeggingPer: string | null;
  factuurgegevens: Record<string, string> | null;
  maxEngineers: number;
  aantalEngineers: number;
  licentie: Licentie;
  /** SPEC §16.3 */
  verwijderOp: string | null;
  lopend: boolean;
  klantportaal: boolean;
  stripeAan: boolean;
  meekijken: boolean;
  prijzen: Record<string, { omschrijving: string | null; perEngineer: boolean } | null>;
}

const STATUS: Record<string, string> = {
  active: 'Actief',
  trialing: 'Actief (proefperiode loopt nog)',
  past_due: 'Betaling achterstallig',
  unpaid: 'Onbetaald',
  canceled: 'Beëindigd',
  incomplete: 'Betaling niet afgerond',
  incomplete_expired: 'Betaling verlopen',
};
const SOORT: Record<AbonnementInfo['abonnement'], string> = { TRIAL: 'Proefperiode', STRIPE: 'Abonnement', HANDMATIG: 'Contract met de leverancier' };
const LEEG_FACTUUR = { bedrijfsnaam: '', email: '', adres: '', postcode: '', plaats: '', btwNummer: '', inkoopnummer: '' };

/** SPEC §12.5: Instellingen › Abonnement (ook het scherm bij een blokkade). */
export function Abonnement() {
  const qc = useQueryClient();
  const [zp, setZp] = useSearchParams();
  const q = useQuery({ queryKey: ['abonnement'], queryFn: () => api.get<AbonnementInfo>('/api/abonnement') });
  const a = q.data;
  const [plan, setPlan] = useState<BetaaldPlan>('STARTER');
  const [mods, setMods] = useState<Module[]>([]);
  const [wijze, setWijze] = useState<'AUTOMATISCH' | 'FACTUUR'>('AUTOMATISCH');
  const [fg, setFg] = useState(LEEG_FACTUUR);
  const klaar = useRef(false);

  useEffect(() => {
    if (!a || klaar.current) return;
    klaar.current = true;
    if (a.plan !== 'TRIAL') setPlan(a.plan as BetaaldPlan);
    setMods(a.abonnement === 'TRIAL' ? [...MODULES] : (a.gekozenModules.filter((m) => (MODULES as readonly string[]).includes(m)) as Module[]));
    if (a.betaalwijze) setWijze(a.betaalwijze);
    if (a.factuurgegevens) setFg({ ...LEEG_FACTUUR, ...a.factuurgegevens });
  }, [a]);

  const ververs = () => {
    void qc.invalidateQueries({ queryKey: ['abonnement'] });
    void qc.invalidateQueries({ queryKey: ['ik'] });
  };

  // terug uit Stripe Checkout: direct verwerken, niet wachten op de webhook
  useEffect(() => {
    const cs = zp.get('checkout');
    const weg = zp.get('geannuleerd');
    if (!cs && !weg) return;
    const nieuw = new URLSearchParams(zp);
    nieuw.delete('checkout');
    nieuw.delete('geannuleerd');
    setZp(nieuw, { replace: true });
    if (weg) return void toast('Afrekenen geannuleerd; er is niets veranderd', 'info');
    api
      .post<{ ok: boolean }>('/api/abonnement/controleer', { sessie: cs })
      .then((r) => toast(r.ok ? 'Bedankt! Het abonnement is actief.' : 'De betaling wordt nog verwerkt; dit kan een minuut duren.', 'succes'))
      .catch((e: Error) => toast(e.message, 'fout'))
      .finally(ververs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const afrekenen = useActie<void, { url?: string; ok?: boolean }>(
    () =>
      a!.lopend
        ? api.post<{ url?: string; ok?: boolean }>('/api/abonnement/wijzigen', { plan, modules: mods })
        : api.post<{ url?: string; ok?: boolean }>('/api/abonnement/afrekenen', { plan, modules: mods, betaalwijze: wijze, factuurgegevens: wijze === 'FACTUUR' ? fg : undefined }),
    {
      succes: (r: { url?: string }) => (r?.url ? undefined : a!.lopend ? 'Abonnement gewijzigd' : 'Abonnement afgesloten. De eerste factuur komt per e-mail.'),
      opNa: (r: { url?: string }) => (r?.url ? void (window.location.href = r.url) : ververs()),
    },
  );
  const portaal = useActie(() => api.post<{ url: string }>('/api/abonnement/portaal'), { opNa: (r) => void (window.location.href = r.url) });

  if (q.isLoading || !a) return <Laden />;
  const lic = a.licentie;
  const prijs = (k: string) => a.prijzen[k]?.omschrijving;
  const kanBestellen = a.stripeAan && a.abonnement !== 'HANDMATIG' && !a.meekijken;
  const ongewijzigd = a.lopend && plan === a.plan && MODULES.every((m) => mods.includes(m) === a.gekozenModules.includes(m));

  return (
    <div className="pagina kolom" style={{ gap: 16, maxWidth: 1000 }}>
      <h1>Abonnement</h1>
      {lic.melding && (
        <div className="paneel" style={{ borderLeft: `4px solid var(${lic.toegang === 'GEBLOKKEERD' ? '--rood' : '--oranje'})` }}>
          <b>{lic.toegang === 'GEBLOKKEERD' ? '⛔ ' : '⚠️ '}</b>
          {lic.melding}
          {a.verwijderOp && (
            <div className="klein" style={{ marginTop: 6 }}>
              Zonder (nieuw) abonnement worden alle gegevens van deze organisatie op <b>{fmtDag(a.verwijderOp, 'jaar')}</b> definitief verwijderd. Sluit je vóór die datum een abonnement af, dan blijft alles bewaard.
            </div>
          )}
        </div>
      )}

      <div className="paneel">
        <div className="grid3">
          <div>
            <div className="klein mute">Soort</div>
            <div className="vet">{SOORT[a.abonnement]}</div>
          </div>
          <div>
            <div className="klein mute">Plan</div>
            <div className="vet">{PLAN_LABEL[a.plan as keyof typeof PLAN_LABEL] ?? a.plan}</div>
          </div>
          <div>
            <div className="klein mute">Status</div>
            <div className="vet">
              {a.abonnement === 'STRIPE' ? STATUS[a.status ?? ''] ?? a.status : a.abonnement === 'TRIAL' ? (lic.toegang === 'GEBLOKKEERD' ? 'Afgelopen' : 'Loopt') : 'Actief'}
            </div>
          </div>
          <div>
            <div className="klein mute">Modules</div>
            <div className="rij" style={{ gap: 6, flexWrap: 'wrap' }}>
              {lic.modules.length ? lic.modules.map((m) => <Badge key={m} toon="blauw">{MODULE_LABEL[m]}</Badge>) : <span className="mute">Alleen de basis</span>}
            </div>
          </div>
          <div>
            <div className="klein mute">{a.abonnement === 'TRIAL' ? 'Proefperiode tot en met' : a.opzeggingPer ? 'Loopt tot' : 'Betaald tot'}</div>
            <div className="vet">{fmtDag(a.abonnement === 'TRIAL' ? a.trialTot : a.opzeggingPer ?? a.betaaldTot, 'jaar') || '–'}</div>
          </div>
          <div>
            <div className="klein mute">Engineers</div>
            <div className="vet">
              {a.aantalEngineers} van max. {a.maxEngineers}
            </div>
          </div>
          {a.abonnement === 'STRIPE' && (
            <div>
              <div className="klein mute">Betaalwijze</div>
              <div className="vet">{a.betaalwijze === 'FACTUUR' ? 'Op factuur (30 dagen)' : 'Automatisch'}</div>
            </div>
          )}
        </div>
        {a.klantportaal && (
          <div className="rij" style={{ marginTop: 14 }}>
            <button className="knop" disabled={portaal.isPending || a.meekijken} onClick={() => portaal.mutate()}>
              🧾 Facturen, betaalmethode en opzeggen
            </button>
            <span className="klein mute">Opent het klantportaal van Stripe</span>
          </div>
        )}
      </div>

      {a.abonnement === 'HANDMATIG' && (
        <div className="paneel">
          Jullie abonnement loopt via een contract met de leverancier. Voor wijzigingen in plan of modules: mail naar <a href="mailto:info@bsmartr.nl">info@bsmartr.nl</a>.
        </div>
      )}
      {a.abonnement !== 'HANDMATIG' && !a.stripeAan && (
        <div className="paneel">
          Online afsluiten is nog niet beschikbaar. Mail naar <a href="mailto:info@bsmartr.nl">info@bsmartr.nl</a> om een abonnement af te sluiten.
        </div>
      )}
      {a.meekijken && <div className="paneel mute">Tijdens meekijken kun je geen abonnement afsluiten of wijzigen.</div>}

      {kanBestellen && (
        <>
          <div className="paneel">
            <h2 style={{ marginBottom: 10 }}>{a.lopend ? 'Plan en modules wijzigen' : 'Kies een plan'}</h2>
            <div className="grid3">
              {BETAALDE_PLANNEN.map((p) => (
                <label key={p} className="paneel" style={{ cursor: 'pointer', margin: 0, borderColor: plan === p ? 'var(--accent)' : undefined, boxShadow: plan === p ? '0 0 0 2px var(--accent)' : undefined }}>
                  <div className="rij">
                    <input type="radio" name="plan" checked={plan === p} onChange={() => setPlan(p)} />
                    <b>{PLAN_LABEL[p]}</b>
                  </div>
                  <div className="klein mute" style={{ marginTop: 4 }}>
                    Tot {PLAN_LIMIET[p]} engineers{p === 'ENTERPRISE' ? ', alle modules inbegrepen' : ''}
                  </div>
                  <div className="klein" style={{ marginTop: 4 }}>{prijs(`PLAN:${p}`) ?? <span className="mute">Prijs op aanvraag</span>}</div>
                </label>
              ))}
            </div>
            <h2 style={{ margin: '16px 0 10px' }}>Modules</h2>
            <div className="kolom" style={{ gap: 8 }}>
              {MODULES.map((m) => (
                <label key={m} className="rij" style={{ alignItems: 'flex-start', gap: 10, opacity: plan === 'ENTERPRISE' ? 0.7 : 1 }}>
                  <input
                    type="checkbox"
                    disabled={plan === 'ENTERPRISE'}
                    checked={plan === 'ENTERPRISE' || mods.includes(m)}
                    onChange={(e) => setMods(e.target.checked ? [...mods, m] : mods.filter((x) => x !== m))}
                  />
                  <span>
                    <b>{MODULE_LABEL[m]}</b> <span className="klein">{plan === 'ENTERPRISE' ? 'inbegrepen' : prijs(`MODULE:${m}`) ?? ''}</span>
                    <div className="klein mute">{MODULE_UITLEG[m]}</div>
                  </span>
                </label>
              ))}
            </div>
            {a.aantalEngineers > PLAN_LIMIET[plan] && (
              <p className="klein" style={{ color: 'var(--rood)', marginTop: 10 }}>
                Jullie hebben {a.aantalEngineers} actieve engineers; {PLAN_LABEL[plan]} is voor maximaal {PLAN_LIMIET[plan]}.
              </p>
            )}
          </div>

          {!a.lopend && (
            <div className="paneel">
              <h2 style={{ marginBottom: 10 }}>Betalen</h2>
              <div className="kolom" style={{ gap: 8 }}>
                <label className="rij" style={{ gap: 10 }}>
                  <input type="radio" name="wijze" checked={wijze === 'AUTOMATISCH'} onChange={() => setWijze('AUTOMATISCH')} />
                  <span>
                    <b>Automatisch</b> <span className="klein mute">iDEAL, creditcard of SEPA-incasso via Stripe</span>
                  </span>
                </label>
                <label className="rij" style={{ gap: 10 }}>
                  <input type="radio" name="wijze" checked={wijze === 'FACTUUR'} onChange={() => setWijze('FACTUUR')} />
                  <span>
                    <b>Op factuur</b> <span className="klein mute">per e-mail, 30 dagen betaaltermijn</span>
                  </span>
                </label>
              </div>
              {wijze === 'FACTUUR' && (
                <div className="grid2" style={{ marginTop: 12 }}>
                  {(
                    [
                      ['bedrijfsnaam', 'Bedrijfsnaam'],
                      ['email', 'Factuur-e-mailadres'],
                      ['adres', 'Adres'],
                      ['postcode', 'Postcode'],
                      ['plaats', 'Plaats'],
                      ['btwNummer', 'Btw-nummer (optioneel)'],
                      ['inkoopnummer', 'Inkoopnummer (optioneel)'],
                    ] as [keyof typeof LEEG_FACTUUR, string][]
                  ).map(([k, l]) => (
                    <Veld key={k} label={l}>
                      <input value={fg[k]} onChange={(e) => setFg({ ...fg, [k]: e.target.value })} />
                    </Veld>
                  ))}
                </div>
              )}
              {a.abonnement === 'TRIAL' && a.trialTot && lic.toegang !== 'GEBLOKKEERD' && (
                <p className="klein mute" style={{ marginTop: 10 }}>De eerste betaling volgt na de proefperiode ({fmtDag(a.trialTot, 'jaar')}).</p>
              )}
            </div>
          )}

          <div className="rij">
            <span className="groei klein mute">Bedragen zijn exclusief btw, tenzij anders vermeld. Wijzigingen worden naar rato verrekend.</span>
            <button className="knop primair" disabled={afrekenen.isPending || ongewijzigd} onClick={() => afrekenen.mutate()}>
              {a.lopend ? 'Wijziging doorvoeren' : wijze === 'AUTOMATISCH' ? 'Verder naar betalen →' : 'Abonnement op factuur afsluiten'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
